import { Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { AbstractSandbox } from 'app/core/sandboxes/abstract.sandbox';
import { OrderRestService } from 'app/core/services/rest/order.rest.service';
import { RelocationRestService } from 'app/core/services/rest/relocation.rest.service';
import { Florder } from 'app/core/interfaces/florder.interface';
import { MaterialTypes } from 'app/core/store/contract-details/contract-details.interface';
import { OrderDetailTO, RelocationDetail } from 'app/core/store/edit-florder-detail/florder-detail.interface';
import * as relocationActions from 'app/core/store/edit-relocation-detail/relocation-detail.actions';
import { RelocationDetailCreateStartAction } from 'app/core/store/edit-relocation-detail/relocation-detail.actions';
import * as templateActions from 'app/core/store/template/template.actions';
import { RelocationTemplateCreateStartAction } from 'app/core/store/template/template.actions';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import * as actions from '../store/edit-relocation-detail/relocation-detail.actions';
import { goAction } from '../store/router/router.actions';
import { distinctUntilChanged, filter, first, map } from 'rxjs/operators';
import { FlordersFilter } from 'app/core/interfaces/florders-filter.interface';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CalendarCreateRelocationModalComponent } from 'app/modules/calendar/relocations/calendar-create-relocation-modal/calendar-create-relocation-modal.component';
import { CreateRelocationsModalData } from 'app/core/store/calendar/relocations/calendar-relocations.interface';
import { getFirst$$, safe } from 'app/core/util/util';
import { mapFlordersTo2Relocations } from 'app/core/store/relocations/relocations.reducer';
import { BusyAction, BusyDoneAction } from 'app/core/store/ui/ui.actions';
import { TemplateRestService } from 'app/core/services/rest/template.rest.service';
import { RelocationsClearFilterAction } from '../store/relocations/relocations.actions';

@Injectable({ providedIn: 'root' })
export class RelocationSandbox extends AbstractSandbox {
  definitions$ = this.store.select(state => state.definitions).pipe(distinctUntilChanged());
  places$ = this.store.select(state => state.customerInfo.places).pipe(distinctUntilChanged());
  templateState$ = this.store.select(state => state.template).pipe(distinctUntilChanged());

  constructor(private relocationRestService: RelocationRestService, private orderRestService: OrderRestService, private templateRestService: TemplateRestService, private dialog: MatDialog) {
    super();
  }

  /**
   * Go to edit planning, but first trigger load of loadingDates
   */
  editPlanning(): Observable<void> {
    return this.loadAndNextAction(new relocationActions.RelocationDetailCreateEditOrderPlanningAction());
  }

  /**
   * Save panel 2: details (materials), but first trigger load of loadingDates
   */
  saveDetails(): Observable<void> {
    return this.loadAndNextAction(new relocationActions.RelocationDetailSaveOrderDetailAction());
  }

  /**
   * Copy a relocation, without using effects
   * @param etmOrderNumber
   */
  copyRelocation(etmOrderNumber: string): void {
    this.store.dispatch(new actions.RelocationDetailCopyOrderAction({ etmOrderNumber }));

    this.store.pipe(first()).subscribe(state => {
      this.relocationRestService
        .getRelocation(etmOrderNumber, 'X') // UAT 1840 + explanation Piet Borgman, mail 10/08/18 16:39 see mail-piet-2018-08-10-copy-order.png
        .subscribe(
          (relocationTO: OrderDetailTO) => {
            this.store.dispatch(
              new actions.RelocationDetailCopyOpenSuccessAction({
                relocationDetail: relocationTO,
                definitions: state.definitions,
              })
            );
            // Next step: set mode to 'edit planning', load dates
            //         return from([new actions.OrderDetailRequestDeliveryDates(new actions.OrderDetailCreateEditOrderPlanningGetDatesSuccess()), new actions.OrderDetailCreateSaveMaterialStart({ material: extendedLineItems[0], preventRestCall: false, showPopup: false }), go('/orders/copy')]);

            this.loadMaterials().subscribe(() => {
              // Next step: go to edit page
              this.store.dispatch(goAction({ path: '/relocation/copy' }));
              this.editPlanning();
            });
          },
          err => {
            this.handleFail(err, 'RELOCATIONS.DETAIL.LABELS.COPY_RELOCATION');
            this.azureMonitoringService.logException(err, { message: 'relocation copy fail' });
            this.store.dispatch(new actions.RelocationDetailOpenFailAction(err));
          }
        );
    });
  }

  createRelocationFromTemplate(templateId: number, date: string) {
    this.store.dispatch(
      goAction({
        path: [`relocation/new/fromTemplate/${templateId}`],
        query: { date },
      })
    );
    setTimeout(() => {
      this.loadMaterials().subscribe(() => {
        this.editPlanning();
      });
    }, 1000);
  }

  /**
   * Load materials for relocation
   * @param depotId depot from where relocation departs.  If not given, take the from-id from the state
   * @returns an observable<void> for enabling you to chain a next event
   */
  loadMaterials(depotId?: string): Observable<void> {
    const subject = new Subject<void>();

    this.store
      .select(state => state.editRelocationDetail.relocationDetail && state.editRelocationDetail.relocationDetail.deliveryMethod)
      .pipe(first())
      .subscribe(relocation => {
        if (relocation && relocation.from && relocation.from.id && relocation.to && relocation.to.id) {
          this.store.dispatch(
            new actions.RelocationDetailGetMaterialsWithFromToAction({
              fromId: relocation.from.id,
              toId: relocation.to.id,
            })
          );
          this.relocationRestService.getMaterialsWithFromToId(relocation.from.id, relocation.to.id).subscribe(
            (data: MaterialTypes) => {
              this.store.dispatch(new actions.RelocationDetailGetMaterialsSuccessAction(data));
              subject.next();
              subject.complete();
            },
            err => {
              this.handleFail(err, `ERRORS.GET_MATERIALS.TITLE`);
              this.azureMonitoringService.logException(err, { message: 'relocation get materials with fromToId fail' });
              this.store.dispatch(new actions.RelocationDetailGetMaterialsFailAction(err));
              subject.error(err);
            }
          );
        }
      });

    return subject.asObservable();
  }

  loadMaterialsFromIdToId(fromId: string, toId: string): Observable<void> {
    const subject = new Subject<void>();

    this.store.dispatch(new actions.RelocationDetailGetMaterialsWithFromToAction({ fromId, toId }));
    this.relocationRestService.getMaterialsWithFromToId(fromId, toId).subscribe(
      (data: MaterialTypes) => {
        this.store.dispatch(new actions.RelocationDetailGetMaterialsSuccessAction(data));
        subject.next();
        subject.complete();
      },
      err => {
        this.handleFail(err, `ERRORS.GET_MATERIALS.TITLE`);
        this.azureMonitoringService.logException(err, { message: 'relocation get materials fail' });
        this.store.dispatch(new actions.RelocationDetailGetMaterialsFailAction(err));
        subject.error(err);
      }
    );

    return subject.asObservable();
  }

  async submitCreateRelocation(): Promise<boolean> {
    this.store.dispatch(new actions.RelocationDetailCreateSubmitAction());

    let relocationDetail = await firstValueFrom(this.store.select(state => state.editRelocationDetail.relocationDetail));
    let unloadingDates = [];

    for (let index = 0; index < relocationDetail.planning.quantity; index++) {
      unloadingDates.push(relocationDetail.planning.unloadingDate as string);
    }

    relocationDetail = {
      ...relocationDetail,
      planning: {
        ...relocationDetail.planning,
        unloadingDate: unloadingDates,
      },
    };

    const { error, result } = await safe(getFirst$$(this.relocationRestService.createRelocation(relocationDetail)));
    if (error) {
      this.handleFail(error, 'RELOCATIONS.LABELS.SUBMIT_RELOCATION');
      this.azureMonitoringService.logException(error, { message: 'relocation submit fail' });
      this.store.dispatch(new actions.RelocationDetailCreateSubmitFailAction(error));
      return false;
    } else {
      if (relocationDetail.createTemplate) {
        await this.submitCreateTemplate(false);
      }
      this.store.dispatch(new actions.RelocationDetailCreateSubmitSuccessAction({ result }));
    }
    return true; // success
  }

  createTemplateFromRelocation(relocation: Florder) {
    this.store.dispatch(new templateActions.TemplateCreateFromRelocationStartAction({ etmOrderNumber: relocation.etmOrderNumber }));
    setTimeout(() => {
      this.loadMaterials().subscribe(() => {
      });
    });
  }

  createTemplate() {
    this.store.dispatch(
      goAction({
        path: ['/relocation/template/new'],
        query: { back: 'relocations' },
      })
    );
  }

  resetCreatePage(isTemplate: boolean) {
    if (isTemplate) {
      this.store.dispatch(new RelocationTemplateCreateStartAction({ selectedCustomer: null }));
    } else {
      this.store.dispatch(new RelocationDetailCreateStartAction({ date: null }));
    }
  }

  private loadLoadingDates(ids: { fromId: string; toId: string }): Observable<void> {
    const result = new Subject<void>();
    this.store.dispatch(new actions.RelocationDetailRequestLoadingDatesAction(ids));

    this.relocationRestService.getLoadingDates(ids).subscribe(
      loadingDatesTO => {
        this.store.dispatch(new actions.RelocationDetailRequestLoadingDatesSuccessAction(loadingDatesTO));
        result.next();
        result.complete();
      },
      error => {
        this.azureMonitoringService.logException(error, { message: 'relocation getLoadingDates fail' });
        this.store.dispatch(new actions.RelocationDetailRequestLoadingDatesFailAction(error));

        this.notificationService.showModal({
          type: 'error',
          titleCode: 'ERROR',
          message: error.Message,
          redirectUrl: '',
          buttonText: 'CLOSE',
          disableClose: false,
        });
        result.error(error);
      }
    );
    return result.asObservable();
  }

  /**
   * Helper method to load loadDates and continue
   * @param action action to trigger after successful load
   */
  private loadAndNextAction(action: Action): Observable<void> {
    const subject = new Subject<void>();
    // Get fromId & toId (once)
    this.store
      .pipe(
        first(),
        map(state => state.editRelocationDetail),
        filter(editRelocationDetail => !!editRelocationDetail.relocationDetail.deliveryMethod.from && !!editRelocationDetail.relocationDetail.deliveryMethod.to)
      )
      .subscribe(editRelocationDetail => {
        const fromId = editRelocationDetail.relocationDetail.deliveryMethod.from.id;
        const toId = editRelocationDetail.relocationDetail.deliveryMethod.to.id;
        // Trigger load
        this.loadLoadingDates({ fromId, toId }).subscribe(() => {
          // Dispatch next action
          this.store.dispatch(action);
          subject.next();
          subject.complete();
        });
      });
    return subject.asObservable();
  }

  ShowCreateRelocationModalAction(data: CreateRelocationsModalData) {
    const config: MatDialogConfig = {
      width: '75%',
      height: 'auto',
      data,
      role: 'dialog',
    };

    const dialogRef = this.dialog.open(CalendarCreateRelocationModalComponent, config);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (result.createOrder) {
          if (result.id) {
            // start from template
            this.createRelocationFromTemplate(result.id, result.date);
          } else {
            // start from scratch
            this.store.dispatch(
              goAction({
                path: [`relocation/new`],
                query: { date: result.date },
              })
            );
          }
        } else if (result.id) {
          // navigate to existing order
          this.store.dispatch(
            goAction({
              path: [`relocation/${result.id}`],
              query: {
                salesOrderNumber: result.salesOrderNumber,
                back: 'calendar/relocation',
              },
            })
          );
        }
      }
    });
  }

  async loadRelocations$$(filter: FlordersFilter) {
    const flordersTos = await getFirst$$(this.relocationRestService.getRelocations(filter));
    const orders = mapFlordersTo2Relocations({
      relocations: flordersTos,
      definitions: await getFirst$$(this.definitions$),
      places: await getFirst$$(this.places$),
    });
    return orders;
  }

  async submitCreateTemplate(showSuccess = true) {
    try {
      this.store.dispatch(new BusyAction('saving template'));
      const relocationDetail = await getFirst$$(this.store.select<RelocationDetail>(state => state.editRelocationDetail.relocationDetail));
      const { error } = await safe(getFirst$$(this.templateRestService.createRelocationTemplate('RELOCATION', relocationDetail)));
      if (error) {
        await this.handleFail(error, 'ERRORS.TEMPLATE.CREATE');
      } else if (showSuccess) {
        this.store.dispatch(new BusyDoneAction('saving template'));
        await this.notificationService.showModal({
          type: 'success',
          messageCode: 'TEMPLATES.DETAILS.EDIT_SUCCESS',
          disableClose: false,
          redirectUrl: '/relocations',
        });
      }
    } finally {
      this.store.dispatch(new BusyDoneAction('saving template'));
    }
  }

  resetFilter() {
    this.store.dispatch(new RelocationsClearFilterAction());
  }
}
